import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import type { TabsProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import api from "../services/api";
import type { PendingUser, User, UserFormValues } from "../types";

export default function Users(): JSX.Element {
  const { t } = useTranslation();
  const { isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm<UserFormValues>();
  const [loading, setLoading] = useState<boolean>(false);

  // Redirect non-admin users
  useEffect(() => {
    // Wait until auth is loaded before checking
    if (isLoading) return;

    if (!isAdmin) {
      message.error(t("users.adminAccessRequired"));
      navigate("/");
    }
  }, [isAdmin, isLoading, navigate, t]);

  const fetchUsers = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      // Fetch approved users
      const usersResponse = await api.get<User[]>("/users");
      const usersArray = Array.isArray(usersResponse) ? usersResponse : [];
      setUsers(usersArray);

      // Fetch pending users
      const pendingResponse = await api.get<{ pending_users: PendingUser[] }>(
        "/auth/pending-users"
      );
      setPendingUsers(pendingResponse.pending_users || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      message.error(t("users.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddEdit = async (values: UserFormValues): Promise<void> => {
    try {
      setLoading(true);
      if (editingUser) {
        await api.patch<User>(`/users/${editingUser.id}`, values);
        message.success(t("users.updateSuccess"));
      } else {
        // Create user with password
        await api.post<User>("/users", values);
        message.success(t("users.createSuccess"));
      }
      setModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      console.error("Operation error:", error);
      message.error(t("users.operationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId: number): Promise<void> => {
    try {
      setLoading(true);
      await api.delete(`/users/${userId}`);
      message.success(t("users.deleteSuccess"));
      fetchUsers();
    } catch (error) {
      console.error("Delete error:", error);
      message.error(t("users.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePending = async (userId: number): Promise<void> => {
    try {
      setLoading(true);
      await api.post(`/auth/approve-user/${userId}`, {});
      message.success(t("users.approveSuccess"));
      fetchUsers();
    } catch (error) {
      console.error("Approve error:", error);
      message.error(t("users.approveFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPending = async (userId: number): Promise<void> => {
    try {
      setLoading(true);
      await api.delete(`/auth/reject-user/${userId}`);
      message.success(t("users.rejectSuccess"));
      fetchUsers();
    } catch (error) {
      console.error("Reject error:", error);
      message.error(t("users.rejectFailed"));
    } finally {
      setLoading(false);
    }
  };

  const pendingColumns: ColumnsType<PendingUser> = [
    {
      title: t("users.columns.username"),
      dataIndex: "username",
      key: "username",
    },
    {
      title: t("users.columns.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("users.columns.registered"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t("users.columns.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleApprovePending(record.id)}
          >
            {t("users.approve")}
          </Button>
          <Popconfirm
            title={t("users.reject.title")}
            description={t("users.reject.description")}
            onConfirm={() => handleRejectPending(record.id)}
            okText={t("users.reject.okText")}
            cancelText={t("users.reject.cancelText")}
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<CloseCircleOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const approvedColumns: ColumnsType<User> = [
    {
      title: t("users.columns.id"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("users.columns.username"),
      dataIndex: "username",
      key: "username",
    },
    {
      title: t("users.columns.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("users.columns.role"),
      dataIndex: "role",
      key: "role",
      render: (role: string) => {
        // Handle both "admin" and "UserRole.ADMIN" formats
        const roleString = typeof role === "string" ? role.split(".").pop()?.toLowerCase() : role;
        return roleString === "admin" ? t("users.roleAdmin") : t("users.roleUser");
      },
    },
    {
      title: t("users.columns.status"),
      dataIndex: "is_active",
      key: "is_active",
      render: (is_active: boolean) => (
        <Tag color={is_active ? "green" : "red"}>
          {is_active ? t("users.statusActive") : t("users.statusInactive")}
        </Tag>
      ),
    },
    {
      title: t("users.columns.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingUser(record);
              form.setFieldsValue({
                username: record.username,
                email: record.email,
                role:
                  record.role === "ADMIN" || record.role === "UserRole.ADMIN" ? "ADMIN" : "USER",
              });
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title={t("users.deleteConfirm.title")}
            description={t("users.deleteConfirm.description")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("users.deleteConfirm.okText")}
            cancelText={t("users.deleteConfirm.cancelText")}
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabsItems: TabsProps["items"] = [
    {
      key: "approved",
      label: t("users.tabs.approved", { count: users.length }),
      children: (
        <Table dataSource={users} columns={approvedColumns} rowKey="id" loading={loading} />
      ),
    },
    {
      key: "pending",
      label: t("users.tabs.pending", { count: pendingUsers.length }),
      children: (
        <Table
          dataSource={pendingUsers}
          columns={pendingColumns}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => {
            setEditingUser(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          {t("users.addUser")}
        </Button>

        <Tabs items={tabsItems} />
      </Space>

      <Modal
        title={editingUser ? t("users.editUser") : t("users.addUser")}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
      >
        <Form form={form} onFinish={handleAddEdit} layout="vertical">
          <Form.Item
            name="username"
            label={t("users.form.username")}
            rules={[{ required: true, message: t("users.form.usernameRequired") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("users.form.email")}
            rules={[
              { required: true, message: t("users.form.emailRequired") },
              { type: "email", message: t("users.form.emailInvalid") },
            ]}
          >
            <Input />
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label={t("users.form.password")}
              rules={[{ required: true, message: t("users.form.passwordRequired") }]}
            >
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item
            name="role"
            label={t("users.form.role")}
            rules={[{ required: true, message: t("users.form.roleRequired") }]}
            initialValue="USER"
          >
            <Select
              options={[
                { label: t("users.form.roleUserOption"), value: "USER" },
                { label: t("users.form.roleAdminOption"), value: "ADMIN" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
